import { Embed, models, Report, service } from 'powerbi-client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePowerBI, usePrincipals } from '../../hooks/queries';
import { getCurrentPrincipal } from '../../lib/currentPrincipal';
import { usePowerBiEmbedToken } from '../../utils/principal-service/powerbi-endpoints';

export enum ReportStatus {
  Loading = 'loading',
  Rendered = 'done',
  Error = 'error',
}

const BasePowerBiConfig = {
  type: 'report',
  tokenType: models.TokenType.Embed,
  settings: {
    layoutType: models.LayoutType.Custom,
    customLayout: {
      displayOption: models.DisplayOption.FitToWidth,
    },
    panes: {
      filters: { visible: false },
      pageNavigation: { visible: false },
    },
    background: models.BackgroundType.Transparent,
  },
} as const;

const IFRAME_HEIGHT_DEFAULT = 200;

export function usePowerBIEmbed() {
  const { data: powerBIConfig } = usePowerBI();
  const { data: principals } = usePrincipals();
  const {
    currentPrincipal: { manufacturerName },
  } = getCurrentPrincipal(principals);
  const { data: accessToken } = usePowerBiEmbedToken(manufacturerName);
  const [reportHeight, setReportHeight] = useState<number>(
    IFRAME_HEIGHT_DEFAULT,
  );
  const embedClientRef = useRef<Report | null>(null);
  const pendingPageRef = useRef<string | null>(null);

  const [reportStatus, setReportStatus] = useState<ReportStatus>(
    ReportStatus.Loading,
  );

  useEffect(() => {
    async function updateDimensions() {
      switch (reportStatus) {
        case ReportStatus.Loading:
        case ReportStatus.Error:
          setReportHeight(IFRAME_HEIGHT_DEFAULT);
          break;
        case ReportStatus.Rendered:
          {
            const activePage = await embedClientRef.current?.getActivePage();

            const resizedHeightWithAspectRatio =
              embedClientRef.current?.iframe.clientWidth &&
              activePage?.defaultSize.height &&
              activePage?.defaultSize.width
                ? Math.ceil(
                    (embedClientRef.current?.iframe.clientWidth *
                      activePage.defaultSize.height) /
                      activePage.defaultSize.width,
                  )
                : IFRAME_HEIGHT_DEFAULT;

            setReportHeight(resizedHeightWithAspectRatio);
          }
          break;
        default: {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const _exhaustiveCheck: never = reportStatus;
        }
      }
    }

    updateDimensions();
  }, [reportStatus]);

  const setReportPage = useCallback(async (reportPage: string | null) => {
    if (!reportPage) {
      return;
    }
    if (!embedClientRef.current) {
      pendingPageRef.current = reportPage;
      return;
    }
    try {
      pendingPageRef.current = null;
      setReportStatus(ReportStatus.Loading);
      await embedClientRef.current?.setPage(reportPage);
    } catch (e) {
      console.error(e);
      setReportStatus(ReportStatus.Error);
    }
  }, []);

  const embedConfig: models.IReportEmbedConfiguration | null = useMemo(
    () =>
      powerBIConfig && accessToken
        ? {
            ...BasePowerBiConfig,
            id: powerBIConfig.reports.reportId,
            embedUrl: `https://app.powerbi.com/reportEmbed?reportId=${powerBIConfig.reports.reportId}&groupId=${powerBIConfig.reports.groupId}`,
            accessToken,
          }
        : null,
    [powerBIConfig, accessToken],
  );

  const setEmbedClient = useCallback((report: Embed) => {
    embedClientRef.current = report as Report;
  }, []);

  const eventHandlers = useMemo(
    () =>
      new Map([
        [
          'rendered',
          () => {
            if (pendingPageRef.current) {
              const page = pendingPageRef.current;
              pendingPageRef.current = null;
              setReportPage(page);
            } else {
              setReportStatus(ReportStatus.Rendered);
            }
          },
        ],
        [
          'error',
          function (event?: service.ICustomEvent<unknown>) {
            console.log(event);
            pendingPageRef.current = null;
            setReportStatus(ReportStatus.Error);
          },
        ],
        [
          'pageChanged',
          () => {
            setReportStatus(ReportStatus.Loading);
          },
        ],
      ]),
    [setReportPage],
  );

  return {
    embedConfig,
    setReportPage,
    setEmbedClient,
    reportStatus,
    eventHandlers,
    reportHeight,
  };
}
